import { Box, Typography } from '@mui/material';

import type { ChatMessage, SystemMessage } from '../types';

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A11y-compliant pastel colors with 4.5:1+ contrast on grey.700 (#616161) */
const NAME_COLORS = ['#90CAF9', '#A5D6A7', '#CE93D8', '#FFCC80', '#80DEEA'];

function getNameColor(senderId: string | undefined): string | undefined {
  if (!senderId) return undefined;
  let hash = 0;
  for (let i = 0; i < senderId.length; i++) {
    hash = (hash * 31 + senderId.charCodeAt(i)) | 0;
  }
  return NAME_COLORS[Math.abs(hash) % NAME_COLORS.length];
}

interface ChatMessageBubbleProps {
  message: ChatMessage;
  isMine: boolean;
  showName?: boolean;
}

export function ChatMessageBubble({ message, isMine, showName = true }: ChatMessageBubbleProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMine ? 'flex-end' : 'flex-start',
        maxWidth: '80%',
        alignSelf: isMine ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          bgcolor: isMine ? 'primary.main' : 'grey.700',
          color: 'common.white',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          maxWidth: '100%',
        }}
      >
        {!isMine && showName && (
          <Typography
            variant="caption"
            sx={{ color: getNameColor(message.senderId) ?? 'grey.300', fontWeight: 600, display: 'block', mb: 0.25 }}
          >
            {message.senderName}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {message.text}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'right',
            mt: 0.25,
            opacity: 0.7,
            fontSize: '0.65rem',
          }}
        >
          {formatTime(message.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
}

interface SystemMessageRowProps {
  message: SystemMessage;
}

export function SystemMessageRow({ message }: SystemMessageRowProps) {
  return (
    <Typography
      variant="caption"
      sx={{
        textAlign: 'center',
        color: 'grey.400',
        py: 0.5,
        alignSelf: 'center',
      }}
    >
      {message.text}
    </Typography>
  );
}
